import React, { useMemo, useState } from 'react';
import { Card, Table, Button, Space, Select, Typography, message, Tag } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { useAuthStore } from '@/store/authStore';
import assetService, { type AssetDto } from '@/services/asset.service';

const { Text } = Typography;

const STATUS_COLOR: Record<string, string> = {
  draft: 'default',
  active: 'green',
  in_use: 'blue',
  under_maintenance: 'orange',
  idle: 'gold',
  disposed: 'red',
};

function toCsvEsc(v: string | null | number | undefined) {
  if (v == null || v === '') {
    return '';
  }
  const t = String(v);
  if (/[",\n\r]/.test(t)) {
    return `"${t.replace(/"/g, '""')}"`;
  }
  return t;
}

const AssetLocationReportPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.view'));
  const [status, setStatus] = useState<string | undefined>(undefined);

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['assets', 'location-report', status],
    queryFn: () => assetService.list({ status: status || undefined }),
    enabled: canView,
  });

  const cols: ColumnsType<AssetDto> = useMemo(
    () => [
      { title: 'Code', dataIndex: 'asset_code', key: 'c', width: 120 },
      { title: 'Name', dataIndex: 'asset_name', key: 'n', ellipsis: true },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 's',
        width: 120,
        render: (s: string) => <Tag color={STATUS_COLOR[s] || 'default'}>{(s || '').replace(/_/g, ' ')}</Tag>,
      },
      { title: 'Site', dataIndex: 'site', key: 'site', ellipsis: true, render: (s: string | null) => s || '—' },
      { title: 'Building', dataIndex: 'building', key: 'b', ellipsis: true, render: (s: string | null) => s || '—' },
      { title: 'Room', dataIndex: 'room', key: 'r', width: 120, render: (s: string | null) => s || '—' },
    ],
    []
  );

  const exportCsv = () => {
    const h = ['asset_code', 'asset_name', 'status', 'site', 'building', 'room'];
    const lines: string[] = [h.join(',')];
    for (const a of rows) {
      lines.push(
        [toCsvEsc(a.asset_code), toCsvEsc(a.asset_name), toCsvEsc(a.status), toCsvEsc(a.site), toCsvEsc(a.building), toCsvEsc(a.room)].join(',')
      );
    }
    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'asset-locations.csv';
    a.click();
    URL.revokeObjectURL(url);
    message.success('CSV ready');
  };

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Card>Access denied. Need asset.view</Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 8 }}>
      <Space>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/asset/reports')}>
          All asset reports
        </Button>
        <Link to="/asset/assets">Register</Link>
      </Space>
      <Card
        style={{ marginTop: 8 }}
        title="Location (current register)"
        extra={
          <Space>
            <Text type="secondary">Status</Text>
            <Select
              allowClear
              style={{ width: 140 }}
              placeholder="All"
              value={status}
              onChange={setStatus}
              options={[
                { value: 'draft', label: 'draft' },
                { value: 'active', label: 'active' },
                { value: 'in_use', label: 'in use' },
                { value: 'under_maintenance', label: 'under maintenance' },
                { value: 'idle', label: 'idle' },
                { value: 'disposed', label: 'disposed' },
              ]}
            />
            <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={rows.length === 0}>
              Export CSV
            </Button>
            <Button onClick={() => void refetch()}>Refresh</Button>
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Shows the current site, building, and room fields from the register (not historical locations). Use the movement log for a dated trail of changes.
        </Text>
        <Table<AssetDto> rowKey="id" loading={isLoading} dataSource={rows} columns={cols} />
      </Card>
    </div>
  );
};

export default AssetLocationReportPage;
